import React, { useMemo, Fragment, FunctionComponent } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from '@rbx/intl';
import { MenuItem, Select } from '@rbx/ui';
import AppLayout, { LayoutType, TOpenGraphMeta } from '@modules/layout/layout/components/AppLayout';
import AppMainNavbar from '@modules/layout/layout/components/AppMainNavbar';
import AppContent from '@modules/layout/layout/components/AppContent';
import AppHeading from '@modules/layout/layout/components/AppHeading';
import AppLeftNav from '@modules/layout/layout/components/AppLeftNav';
import AppRightNav from '@modules/layout/layout/components/AppRightNav';
import AppRightNavContent, { RenderItem } from '@modules/ui/shared/components/AppRightNavContent';
import SideBarNavigation from '@modules/ui/shared/components/SideBarNavigation';
import stripMarkdown from '@modules/ui/shared/utils/stripMarkdown';
import useSideBarContent from '@modules/ui/shared/hooks/useSideBarContent';
import { TestId } from '@modules/utilities/constants/testConstants';
import EndpointTreeItem from '@modules/ui/shared/components/EndpointTreeItem';
import AppIAHeading from '@modules/layout/layout/components/AppIAHeading';
import CloudIABanner from '@modules/openCloud/CloudIABanner';
import ObjectReferenceProvider from '../../../context/components/ObjectReferenceProvider';
import OpenCloudAPIContent from '../../../utils/interfaces/OpenCloudAPIContent';
import { OpenAPI } from '../../../utils/oasTypes';
import deriveHeadings from '../utils/deriveHeadings';
import OpenCloudContent from './OpenCloudContent';

const renderItem: RenderItem = ({ title, label }) => {
  if (typeof label === 'undefined') {
    return <Fragment>{title}</Fragment>;
  }

  const method = OpenAPI.HttpMethods[label.toUpperCase() as keyof typeof OpenAPI.HttpMethods];
  return <EndpointTreeItem method={method} title={title} />;
};

export interface OpenCloudContainerProps {
  content: OpenCloudAPIContent;
  allVersions: string[];
  openGraphMeta: Partial<TOpenGraphMeta>;
}

const OpenCloudContainer: FunctionComponent<React.PropsWithChildren<OpenCloudContainerProps>> = ({
  content,
  allVersions,
  openGraphMeta,
}) => {
  const router = useRouter();
  const { translate } = useTranslation();
  const { getBreadcrumbsItems } = useSideBarContent();
  const breadcrumbsItems = useMemo(() => {
    return getBreadcrumbsItems(translate);
  }, [getBreadcrumbsItems, translate]);

  const { title, description } = content;

  const headings = useMemo(() => deriveHeadings(translate, content), [translate, content]);
  const metaDescription = useMemo(
    () => (description != null ? stripMarkdown(description) : null),
    [description],
  );

  return (
    <AppLayout
      testid={TestId.OpenCloudContainer}
      pageTitle={title}
      pageMetaDescription={metaDescription}
      headings={headings}
      openGraphMeta={openGraphMeta}
      type={LayoutType.OpenCloudV1}
      renderBanner={() => (process.env.enableOpenCloudIAV2Pages ? <CloudIABanner /> : undefined)}>
      <AppMainNavbar pageTitle={title} breadcrumbsItems={breadcrumbsItems} />
      <AppIAHeading breadcrumbsItems={breadcrumbsItems} />
      <AppHeading
        title={title}
        adornment={
          allVersions.length > 1 && (
            <Select
              value={router.query.version}
              variant='outlined'
              label={translate('Label.Version')}
              margin='dense'>
              {allVersions.map((option) => (
                <MenuItem
                  key={option}
                  value={option}
                  onClick={() => {
                    router.push({
                      pathname: router.pathname,
                      query: { name: router.query.name, version: option },
                    });
                  }}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          )
        }
      />
      <AppRightNav content={<AppRightNavContent renderItem={renderItem} allowExpand={false} />} />
      <AppContent>
        <ObjectReferenceProvider objects={content.objects}>
          <OpenCloudContent content={content} />
        </ObjectReferenceProvider>
      </AppContent>
      <AppLeftNav>
        <SideBarNavigation />
      </AppLeftNav>
    </AppLayout>
  );
};

export default OpenCloudContainer;
